import { useState } from "react";
import type { Category } from "@/types/content";
import type { SortKey } from "@/lib/content";

export interface FilterState {
  subType: string;
  timeRange: string;
  hotOnly: boolean;
  sort: SortKey;
}

interface FilterBarProps {
  category: Category;
  filters: FilterState;
  onChange: (next: Partial<FilterState>) => void;
  onReset: () => void;
}

const timeRanges = ["全部", "一周内", "一个月内", "三个月内"];
const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "最热" },
  { key: "recommend", label: "推荐" },
];

export default function FilterBar({ category, filters, onChange, onReset }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const subTypes = ["全部", ...category.subTypes];

  const panel = (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs font-semibold text-foreground-500 mb-3">
          {category.name}类型
        </p>
        <div className="flex flex-wrap gap-2">
          {subTypes.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onChange({ subType: type })}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                filters.subType === type
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-foreground-500 mb-3">发布时间</p>
        <div className="flex flex-wrap gap-2">
          {timeRanges.map((range) => (
            <button
              key={range}
              type="button"
              onClick={() => onChange({ timeRange: range })}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                filters.timeRange === range
                  ? "bg-secondary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-foreground-500">只看高热度</p>
          <p className="text-xs text-foreground-400 mt-1">浏览量 3 万以上</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={filters.hotOnly}
          onClick={() => onChange({ hotOnly: !filters.hotOnly })}
          className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative ${
            filters.hotOnly ? "bg-accent-500" : "bg-background-300"
          }`}
        >
          <span
            className={`absolute top-0.5 w-5 h-5 rounded-full bg-background-50 transition-all ${
              filters.hotOnly ? "left-[22px]" : "left-0.5"
            }`}
          />
        </button>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center justify-center gap-2 text-sm text-foreground-600 hover:text-primary-600 border border-background-200 rounded-md py-2 transition-colors cursor-pointer"
      >
        <i className="ri-refresh-line"></i>
        重置筛选
      </button>
    </div>
  );

  return (
    <>
      {/* 桌面端筛选栏 */}
      <div className="hidden lg:flex items-start gap-6 bg-background-50 border border-background-200 rounded-lg p-5">
        <div className="w-20 shrink-0 pt-1">
          <span className="text-sm font-semibold text-foreground-900 flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-500"></i>
            筛选
          </span>
        </div>
        <div className="flex-1">{panel}</div>

        <div className="w-px self-stretch bg-background-200"></div>

        <div className="shrink-0">
          <p className="text-xs font-semibold text-foreground-500 mb-3">排序</p>
          <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChange({ sort: opt.key })}
                className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  filters.sort === opt.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 移动端筛选触发与抽屉 */}
      <div className="lg:hidden flex items-center gap-3">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="flex-1 inline-flex items-center justify-center gap-2 bg-background-50 border border-background-200 rounded-md py-2.5 text-sm text-foreground-700 cursor-pointer"
        >
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </button>
        <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
          {sortOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange({ sort: opt.key })}
              className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                filters.sort === opt.key
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/50 animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 animate-slide-down max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold text-lg text-foreground-950">
                筛选条件
              </h3>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="关闭筛选"
                className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {panel}
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="mt-5 w-full bg-primary-500 hover:bg-primary-600 text-background-50 rounded-md py-3 text-sm cursor-pointer whitespace-nowrap"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}